import { type FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { ProductPath } from '#app/types/AppTypes'
import { Select, type SelectEntry } from '#components/Select'
import { SidebarSearch } from '#components/SidebarSearch'
import { testIds } from '#utils/testIds'
import type { FilterState } from '../Details/Filter/FilterState'
import type { ComparisonDisplayMode, ComparisonTableContext } from './ComparisonTableContext'
import { ComparisonTree } from './ComparisonTree'
import type { ConfigurationDiff } from './useConfigurationDifference'
import type { ComparisonDataEntry } from './useLinearizeComparisonData'

const useComparisonOptions = () => {
  const { t } = useTranslation()

  return useMemo(
    () =>
      [
        {
          id: 'differences',
          value: t('configuration.comparison.sidebar.differences', 'Differences'),
        },
        {
          id: 'differences-no-missing',
          value: t('configuration.comparison.sidebar.differences-no-missing', 'Differences (without missing)'),
        },
        {
          id: 'price-differences',
          value: t('configuration.comparison.sidebar.priceDifferences', 'Price Differences'),
        },
        {
          id: 'selected',
          value: t('configuration.comparison.sidebar.selected', 'Selected'),
        },
        {
          id: 'all',
          value: t('configuration.comparison.sidebar.all', 'All'),
        },
      ] satisfies SelectEntry<ComparisonDisplayMode>[],
    [t],
  )
}

export const ComparisonSidebar: FC<{
  displayedEntries: ComparisonDataEntry[]
  displayMode: ComparisonDisplayMode
  setDisplayMode: (mode: ComparisonDisplayMode) => unknown
  filterState: FilterState
  setFilterState: (state: FilterState) => unknown
  diff: ConfigurationDiff | undefined
  selectedEntries: Partial<Record<ProductPath, boolean>>
  scrollTo: ComparisonTableContext['virtualizer']['scrollTo']
  beforeSearch?: React.ReactNode
  afterSearch?: React.ReactNode
  afterSelect?: React.ReactNode
  children?: React.ReactNode
  className?: string
}> = ({
  displayedEntries,
  displayMode,
  setDisplayMode,
  filterState,
  setFilterState,
  diff,
  selectedEntries,
  scrollTo,
  beforeSearch,
  afterSelect,
  afterSearch,
  children,
  className,
}) => {
  const options = useComparisonOptions()

  return (
    <>
      {/* <AsideComponent side="left" viewClassName={comparisonSidebarClass} data-testid={testIds.comparison.sidebar}> */}
      {beforeSearch}

      <SidebarSearch filterState={filterState} onFilterChange={setFilterState} />

      {afterSearch}

      <div className="_group _mode-selector">
        <Select
          className="_selection"
          options={options}
          onChange={(id) => setDisplayMode(id ?? 'differences')}
          slim
          value={displayMode}
          data-testid={testIds.comparison.modeSelector}
        />
      </div>

      {afterSelect}

      <ComparisonTree
        displayedEntries={displayedEntries}
        displayMode={displayMode}
        diff={diff}
        selectedEntries={selectedEntries}
        scrollTo={scrollTo}
        className={className}
      />

      {children}
      {/* </AsideComponent> */}
    </>
  )
}
